import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getPartnerTeams } from "../../services/PartnerService";
import { canManagePartnerDetails } from "../../utils/auth/accessRules";
import { PERMISSION_SLUGS } from "../../utils/auth/permissionConstants";
import PrerequisiteAlert from "./PrerequisiteAlert";

const PREVIEW_PAGE_SIZE = 5;
const FULL_PAGE_SIZE = 10;

const PartnerTeamsTable = ({
  partnerId,
  authUser,
  userPartnerDetails,
  headerTitle = "Teams",
  cardRef,
  mode = "full",
  onSeeAll,
  onAddTeam,
  onEditTeam,
  onDeleteTeam,
  hasExpenseTags = true,
  expenseTagsKnown = true,
  onAddExpenseTag,
}) => {
  const missingExpenseTag = expenseTagsKnown && !hasExpenseTags;
  const isPreview = mode === "preview";
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const perPage = isPreview ? PREVIEW_PAGE_SIZE : FULL_PAGE_SIZE;

  const {
    data,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["partnerTeams", partnerId, isPreview ? "preview" : currentPage, perPage],
    queryFn: () => getPartnerTeams(partnerId, isPreview ? 1 : currentPage, perPage),
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const teams = data?.data?.data ?? null;
  const meta = data?.data?.meta ?? null;

  const canManageTeams = canManagePartnerDetails(
    authUser,
    userPartnerDetails,
    PERMISSION_SLUGS.MANAGE_TEAMS
  );

  const goToPage = (nextPage) => {
    setCurrentPage(nextPage);
    if (cardRef?.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="card" ref={cardRef} style={{ scrollMarginTop: "5rem" }} data-tour="admin-details-teams">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h4 className="card-title">{headerTitle}</h4>
        {onAddTeam && canManageTeams && (
          <>
            <button
              type="button"
              className="btn btn-primary d-none d-lg-inline-flex"
              onClick={onAddTeam}
              disabled={!expenseTagsKnown || missingExpenseTag}
              title={missingExpenseTag ? "Add an expense tag first." : undefined}
              data-tour="admin-details-add-team-button"
            >
              Add Team
            </button>
            <button
              type="button"
              className="btn btn-primary rounded-circle d-inline-flex d-lg-none align-items-center justify-content-center shadow-sm"
              style={{ width: "2.5rem", height: "2.5rem", padding: 0 }}
              title={
                !expenseTagsKnown
                  ? undefined
                  : (missingExpenseTag ? "Add an expense tag first." : "Add team")
              }
              aria-label={missingExpenseTag ? "Add team (add an expense tag first)" : "Add team"}
              onClick={onAddTeam}
              disabled={!expenseTagsKnown || missingExpenseTag}
              data-tour="admin-details-add-team-button"
            >
              <i className="fas fa-plus" aria-hidden="true" />
            </button>
          </>
        )}
      </div>
      <div className="card-body p-0" style={{ position: "relative" }}>
        {onAddTeam && canManageTeams && missingExpenseTag && (
          <PrerequisiteAlert
            className="m-3 mb-0"
            message="Add at least one expense tag before creating a team. Every team spends against a tag, so setup starts there."
            actionLabel="Add expense tag"
            onAction={onAddExpenseTag}
          />
        )}
        {isFetching && teams && teams.length > 0 && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 10,
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex", alignItems: "flex-start", justifyContent: "center",
            paddingTop: "1rem",
          }}>
            <span className="spinner-border spinner-border-sm text-primary" role="status" />
          </div>
        )}
        {isLoading ? (
          <div className="d-flex justify-content-center py-4">
            <span className="spinner-border spinner-border-sm text-primary" role="status" />
          </div>
        ) : teams && teams.length > 0 ? (
          <ul className="list-group list-group-flush">
            {teams.map((team) => (
              <li
                key={team.id}
                className="list-group-item d-flex justify-content-between align-items-center gap-3 flex-nowrap py-3"
              >
                <span
                  className="fw-semibold text-body flex-grow-1"
                  style={{ minWidth: 0, overflowWrap: "anywhere" }}
                >
                  {team.name}
                </span>
                <div className="d-inline-flex flex-shrink-0 gap-2">
                  {onEditTeam && canManageTeams && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      title="Edit team"
                      aria-label={`Edit ${team.name}`}
                      onClick={() => onEditTeam(team)}
                    >
                      <i className="fas fa-pencil-alt" aria-hidden="true" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-info"
                    title="View team"
                    aria-label={`View ${team.name}`}
                    onClick={() => navigate(`/team-details/${team.id}/partners/${partnerId}`)}
                  >
                    <i className="fas fa-eye" aria-hidden="true" />
                  </button>
                  {onDeleteTeam && canManageTeams && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      title="Delete team"
                      aria-label={`Delete ${team.name}`}
                      onClick={() => onDeleteTeam(team)}
                    >
                      <i className="fas fa-trash-alt" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-center text-muted py-4 mb-0">No teams found.</p>
        )}
        {!isPreview && meta && meta.last_page > 1 && (
          <div className="d-flex justify-content-center align-items-center gap-3 py-3 border-top">
            <button
              type="button"
              className="btn btn-sm btn-link text-decoration-none p-0"
              onClick={() => goToPage(meta.current_page - 1)}
              disabled={meta.current_page <= 1}
              aria-label="Previous page"
            >
              <i className="fas fa-chevron-left" aria-hidden="true" />
            </button>
            <span className="small text-muted">
              Page {meta.current_page} of {meta.last_page}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-link text-decoration-none p-0"
              onClick={() => goToPage(meta.current_page + 1)}
              disabled={meta.current_page >= meta.last_page}
              aria-label="Next page"
            >
              <i className="fas fa-chevron-right" aria-hidden="true" />
            </button>
          </div>
        )}
        {isPreview && onSeeAll && (
          <div className="d-flex justify-content-center py-3 border-top">
            <button
              type="button"
              className="btn btn-sm btn-link text-decoration-none"
              onClick={onSeeAll}
            >
              See all teams
              <i className="fas fa-chevron-right ms-1" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerTeamsTable;
